import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Logo } from "@/components/Logo";
import { ThemeToggle } from "@/components/ThemeToggle";
import { useTheme } from "@/context/ThemeContext";
import { brandImage } from "@/lib/brand";
import { QrLoginPanel } from "@/components/QrLoginPanel";
import { useAuth } from "@/context/AuthContext";
import { formatError } from "@/lib/api";

const mediaSrc = (u) => (u?.startsWith("http") ? u : `${process.env.REACT_APP_BACKEND_URL}${u}`);
const isVideo = (u) => /\.(mp4|mov|webm)(\?|$)/i.test(u || "");

export default function Login({ admin = false }) {
  const { login, branding } = useAuth();
  const { theme } = useTheme();
  const nav = useNavigate();
  const [identifier, setId] = useState("");
  const [password, setPwd] = useState("");
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState(() => (admin || window.innerWidth < 1024 ? "email" : "qr"));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await login(identifier, password, admin);
      toast.success("Prijava uspješna");
      const pending = localStorage.getItem("stream_qr_pending");
      if (pending) { localStorage.removeItem("stream_qr_pending"); nav(`/qr/${pending}`); return; }
      nav(u.role === "ADMIN" ? "/admin/dashboard" : "/dashboard");
    } catch (err) { toast.error(formatError(err)); }
    setBusy(false);
  };

  const bg = brandImage(branding, theme, "login_bg") || "";

  return (
    <div className="stream-dark min-h-screen flex items-center justify-center px-4 py-8 sm:py-10 relative overflow-hidden">
      {bg && (isVideo(bg) ? (
        <video src={bg} autoPlay loop muted playsInline className="absolute inset-0 w-full h-full object-cover z-0" data-testid="login-bg-video" />
      ) : (
        <img src={bg} alt="" className="absolute inset-0 w-full h-full object-cover z-0" data-testid="login-bg" />
      ))}
      {bg && <div className="absolute inset-0 bg-black/10 z-0" />}
      <div className={`w-full relative z-10 ${mode === "qr" ? "max-w-3xl" : "max-w-md"}`}>
        <div className="flex justify-end mb-3"><ThemeToggle /></div>
        <Link to="/" className="flex justify-center mb-6 sm:mb-8"><Logo size="xl" light /></Link>
        {mode === "qr" ? (
          <QrLoginPanel onUseEmail={() => setMode("email")} />
        ) : (
        <div className="card-dark p-6 sm:p-8 backdrop-blur-xl bg-[#171717]/85 border-white/10">
          <h1 className="font-heading font-black text-2xl sm:text-3xl text-white mb-2">{admin ? "ADMIN PRIJAVA" : "Prijava"}</h1>
          <p className="text-white/60 mb-8">{admin ? "PRISTUP ADMINISTRATORU" : "Dobrodošli natrag u STRE PAY."}</p>
          <form onSubmit={submit} className="space-y-5">
            <div>
              <Label className="text-white/80">Username ili Email</Label>
              <Input value={identifier} onChange={(e) => setId(e.target.value)} required
                className="mt-2 bg-white/5 border-white/10 text-white h-12 rounded-xl focus-visible:ring-[#FFD60A]"
                data-testid="login-identifier" />
            </div>
            <div>
              <Label className="text-white/80">Lozinka</Label>
              <Input type="password" value={password} onChange={(e) => setPwd(e.target.value)} required
                className="mt-2 bg-white/5 border-white/10 text-white h-12 rounded-xl focus-visible:ring-[#FFD60A]"
                data-testid="login-password" />
            </div>
            <Button type="submit" disabled={busy} className="btn-yellow w-full h-12 rounded-xl text-base" data-testid="login-submit">
              {busy ? "Prijava…" : "PRIJAVA"}
            </Button>
          </form>
          <div className="mt-6 flex justify-between text-sm">
            {!admin && <Link to="/forgot-password" className="text-white/60 hover:text-[#FFD60A]" data-testid="link-forgot">Zaboravljena lozinka?</Link>}
            {!admin && <Link to="/register" className="text-white/60 hover:text-[#FFD60A]" data-testid="link-register">Nemate račun?</Link>}
          </div>
          {!admin && (
            <button onClick={() => setMode("qr")} className="text-[#FFD60A] text-sm mt-5 hover:underline" data-testid="use-qr-login">
              Prijava QR kodom (skeniraj mobitelom) &nbsp;›
            </button>
          )}
        </div>
        )}
        <p className="text-center text-white/40 text-xs mt-5">
          <Link to="/privacy" className="privacy-pill hover:text-[#FFD60A]" data-testid="login-privacy-link">Politika privatnosti</Link>
        </p>
      </div>
    </div>
  );
}
